<template>
  <b-card class="lead-source-pie-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body :title="'Lead Source Distribution Chart'" role="button" tabindex="0">
    <template #header>
      <h6 class="mb-0">Lead Source Distribution</h6>
    </template>
    <b-card-body>
      <apexchart v-if="chartReady && safeSeries && safeSeries.length" type="pie" :key="chartKey" height="350" :options="chartOptions" :series="safeSeries"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'LeadSourcePieChart',
  emits: ['chart-point-click'],
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      series: [], // reactive: pie values for each slice
      chartData: [], // [{ id, source_name, count }, ...] for click lookup
      chartOptions: {
        chart: {
          type: 'pie',
          height: 350,
          events: {
            dataPointSelection: (e, ctx, config) => this.handleDataPointSelection(e, ctx, config),
            click: (e, ctx, config) => this.handleChartClick(e, ctx, config),
          },
        },
        labels: [], // Lead source names
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }],
        legend: {
          position: 'right',
        },
      },
    };
  },
  computed: {
    safeSeries() {
      const s = Array.isArray(this.series) ? this.series : [];
      const nums = s.map((v) => (typeof v === 'number' && !Number.isNaN(v) ? v : Number(v) || 0));
      return nums.length ? nums : [0];
    },
    chartReady() {
      const chart = this.chartOptions && this.chartOptions.chart;
      return chart && typeof chart.height === 'number' && chart.height > 0;
    },
    chartKey() {
      const len = Array.isArray(this.series) ? this.series.length : 0;
      return `lead-source-pie-${len}-${(this.chartOptions.labels || []).length}`;
    },
  },
  watch: {
    dashboardData: {
      deep: true,
      immediate: true,
      handler(val) {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (!val || val.lead_source_distribution === undefined) return; // don't clear when parent sets {} on error
        const data = this.normalizeToArray(val.lead_source_distribution);
        this.processChartData(data);
      },
    },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.lead_source_distribution !== undefined) {
            this.processChartData(this.normalizeToArray(this.dashboardData.lead_source_distribution));
          } else {
            this.fetchLeadSourceDistributionData();
          }
          return;
        }
        if (this.dashboardData && this.dashboardData.lead_source_distribution !== undefined) {
          this.processChartData(this.normalizeToArray(this.dashboardData.lead_source_distribution));
          return;
        }
        this.fetchLeadSourceDistributionData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate && this.dashboardData && this.dashboardData.lead_source_distribution !== undefined) {
            this.processChartData(this.normalizeToArray(this.dashboardData.lead_source_distribution));
            return;
          }
          this.fetchLeadSourceDistributionData();
        } else if (newVal != null) {
          this.chartOptions = { ...this.chartOptions, labels: [] };
          this.series = [];
        }
      },
    },
  },
  methods: {
    hasRight, // Make the helper available in the template
    /** Normalize API value to array (Laravel may return object with numeric keys) */
    normalizeToArray(val) {
      if (Array.isArray(val)) return val;
      if (val && typeof val === 'object') return Object.values(val);
      return [];
    },
    async fetchLeadSourceDistributionData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate && this.dashboardData && this.dashboardData.lead_source_distribution !== undefined) {
        this.processChartData(this.normalizeToArray(this.dashboardData.lead_source_distribution));
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/lead-source-distribution', this.filters);
        const raw = response.data && response.data.data;
        this.processChartData(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error('Error fetching lead source distribution data:', error);
        // Handle error
      }
    },
    processChartData(data) {
      const arr = Array.isArray(data) ? data : [];
      this.chartData = arr;
      if (arr.length === 0) {
        this.chartOptions = { ...this.chartOptions, labels: [] };
        this.series = [];
        return;
      }

      const labels = arr.map(item => (item && item.source_name) != null ? String(item.source_name) : '');
      const seriesData = arr.map(item => Number(item && item.count) || 0);

      this.chartOptions = {
        ...this.chartOptions,
        labels: labels,
      };

      this.series = seriesData;
    },
    emitLeadSourceDetail(leadSourceId, leadSourceName) {
      if (leadSourceId === undefined || leadSourceId === null) return;
      this.$emit('chart-point-click', `Lead Source: ${leadSourceName || 'Unknown'}`, `/dashboard/crm/lead-source-distribution/detail/${leadSourceId}`, { source_id: leadSourceId });
    },
    getIdFromItem(item) {
      if (!item) return undefined;
      if (item.id !== undefined && item.id !== null) return item.id;
      if (item.leadsource !== undefined && item.leadsource !== null) return item.leadsource;
      return undefined;
    },
    handleDataPointSelection(event, chartContext, config) {
      const idx = config && (config.dataPointIndex ?? config.seriesIndex);
      if (idx !== undefined && this.chartData[idx]) {
        const item = this.chartData[idx];
        const id = this.getIdFromItem(item);
        this.emitLeadSourceDetail(id, item && item.source_name != null ? item.source_name : 'Unknown');
      }
    },
    handleChartClick(event, chartContext, config) {
      const idx = config && (config.dataPointIndex ?? config.seriesIndex);
      if (idx !== undefined && this.chartData[idx]) {
        const item = this.chartData[idx];
        const id = this.getIdFromItem(item);
        this.emitLeadSourceDetail(id, item && item.source_name != null ? item.source_name : 'Unknown');
      }
    },
  },
};
</script>

<style scoped>
.lead-source-pie-chart {
  margin-top: 20px;
  cursor: pointer; /* Added for clickable affordance */
}
</style>